import { UnknownTriggerError } from '../../errors'
import type { Extension } from '../extension'
import { z } from '../params'
import type { SystemMethodSpec } from '../system-method'
import { POLLING_SYSTEM_METHOD } from './method-names'
import { PollingInvocationSchema } from './system-params'
import type { PollingTriggerMethod } from './trigger-method'
import type { PollingInvocation } from './types'

// The polling triggers a service declares, as a set.
//
// The SYSTEM seam that drives them belongs HERE rather than to any one trigger: the runtime ticks a single
// `handleTriggerPollingForEvent` and this routes it by `eventName`. Registering it on the first trigger
// added is what makes it exist exactly when at least one trigger does — the model no longer needs a
// separate "are there any?" check, because the method is simply there or not.

export class PollingTriggers {
  readonly #triggers = new Map<string, PollingTriggerMethod>()

  constructor(private readonly extension: Extension) {}

  get size(): number {
    return this.#triggers.size
  }

  get(id: string): PollingTriggerMethod | undefined {
    return this.#triggers.get(id)
  }

  all(): PollingTriggerMethod[] {
    return [...this.#triggers.values()]
  }

  add(trigger: PollingTriggerMethod): void {
    const first = this.size === 0

    this.#triggers.set(trigger.id, trigger)

    if (first) {
      this.#registerSystemMethods()
    }
  }

  // ── SYSTEM seam: name · input · handler, in one place ──────────────────────

  #registerSystemMethods(): void {
    this.extension.registerSystemMethods([{
      name: POLLING_SYSTEM_METHOD,
      scheme: z.object({ invocation: PollingInvocationSchema }),
      run: ({ invocation }, ctx) => {
        const value = invocation as PollingInvocation
        const trigger = this.get(value.eventName)

        if (!trigger) {
          throw new UnknownTriggerError(this.extension.id, value.eventName, 'polling')
        }

        return trigger.poll(value, ctx)
      },
    } satisfies SystemMethodSpec])
  }
}
